import React, { useRef } from 'react';
import { Button } from '../components/ui/button';
import { Download, Printer, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const ProductManualPage = () => {
  const navigate = useNavigate();
  const manualRef = useRef(null);

  const handlePrint = () => {
    window.print();
  };

  return (
    <>
      {/* Print Controls - Hidden when printing */}
      <div className="print:hidden fixed top-4 right-4 z-50 flex gap-3">
        <Button variant="outline" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <Button onClick={handlePrint} className="bg-blue-600 hover:bg-blue-700">
          <Printer className="w-4 h-4 mr-2" />
          Print / Save as PDF
        </Button>
      </div>

      {/* Manual Content */}
      <div ref={manualRef} className="manual-content bg-white">
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');
          
          @media print {
            @page {
              size: A4;
              margin: 0;
            }
            body {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            .page-break {
              page-break-before: always;
            }
            .no-break {
              page-break-inside: avoid;
            }
          }
          
          .manual-content {
            font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
            color: #1e293b;
            line-height: 1.6;
          }
          
          .manual-page {
            width: 210mm;
            min-height: 297mm;
            margin: 0 auto;
            padding: 0;
            background: white;
            position: relative;
            overflow: hidden;
          }
          
          @media screen {
            .manual-page {
              box-shadow: 0 4px 20px rgba(0,0,0,0.1);
              margin-bottom: 20px;
            }
          }
        `}</style>

        {/* ==================== COVER PAGE ==================== */}
        <div className="manual-page relative">
          {/* Background Pattern */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-br from-blue-500/10 to-blue-600/5 rounded-full -translate-y-1/2 translate-x-1/4"></div>
            <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-gradient-to-tr from-blue-600/10 to-transparent rounded-full translate-y-1/2 -translate-x-1/4"></div>
            {/* Grid Pattern */}
            <svg className="absolute inset-0 w-full h-full opacity-[0.03]" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                  <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#3b82f6" strokeWidth="1"/>
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#grid)" />
            </svg>
          </div>
          
          <div className="relative z-10 h-full flex flex-col" style={{ minHeight: '297mm' }}>
            {/* Header */}
            <div className="px-16 pt-12 flex justify-between items-start">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center shadow-lg">
                  <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <div className="text-xl font-bold text-slate-800">EDUBRIDGE NETWORK</div>
                  <div className="text-xs text-slate-500">Education CRM</div>
                </div>
              </div>
              <div className="text-right text-sm text-slate-400">
                <div>Product Manual</div>
                <div>Version 2.0</div>
              </div>
            </div>
            
            {/* Main Content */}
            <div className="flex-1 flex flex-col justify-center px-16">
              <div className="mb-6">
                <span className="inline-block px-4 py-1.5 bg-blue-50 text-blue-600 rounded-full text-sm font-medium">
                  AI-Powered Platform
                </span>
              </div>
              
              <h1 className="text-6xl font-extrabold text-slate-900 leading-tight mb-6">
                EDUBRIDGE<span className="text-blue-600">.NETWORK</span>
              </h1>
              
              <p className="text-2xl text-slate-600 font-light leading-relaxed max-w-lg mb-8">
                AI-Enabled Software for Educational Consultancies and Universities with <span className="text-blue-600 font-bold italic">BPO</span> Capabilities
              </p>
              
              <div className="flex items-center gap-4 mb-12">
                <div className="h-1 w-20 bg-gradient-to-r from-blue-600 to-blue-400 rounded-full"></div>
                <span className="text-slate-400 text-sm">Product Manual 2025</span>
              </div>
              
              {/* Feature Pills */}
              <div className="flex flex-wrap gap-3">
                {['AI Automation', 'Smart Analytics', 'Global Reach', 'Secure Cloud'].map((item, i) => (
                  <span key={i} className="px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-600 shadow-sm">
                    {item}
                  </span>
                ))}
              </div>
            </div>
            
            {/* Footer */}
            <div className="px-16 pb-12">
              <div className="flex justify-between items-end">
                <div>
                  <p className="text-sm text-slate-500 mb-1">A Joint Product of</p>
                  <p className="text-base font-semibold text-slate-700">Cloud Lab Private Ltd</p>
                  <p className="text-base font-semibold text-slate-700">International Education Office (IEO) – UK</p>
                </div>
                <div className="text-right">
                  <div className="flex items-center gap-2 text-blue-600">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                    </svg>
                    <span className="font-medium">edubridgenetwork.com</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ==================== TABLE OF CONTENTS ==================== */}
        <div className="manual-page page-break">
          <div className="px-16 py-12">
            <div className="mb-2 text-blue-600 font-medium text-sm tracking-wider">OVERVIEW</div>
            <h2 className="text-4xl font-bold text-slate-900 mb-12">Table of Contents</h2>
            
            <div className="space-y-0">
              {[
                { num: '01', title: 'About EDUBRIDGE NETWORK', page: '04' },
                { num: '02', title: 'Platform Overview', page: '05' },
                { num: '03', title: 'Core Modules', page: '06' },
                { num: '04', title: 'Application Management', page: '07' },
                { num: '05', title: 'AI Counselor', page: '08' },
                { num: '06', title: 'BPO Services', page: '09' },
                { num: '07', title: 'Reporting & Analytics', page: '10' },
                { num: '08', title: 'Student Portal', page: '11' },
                { num: '09', title: 'Key Benefits', page: '12' },
                { num: '10', title: 'Security & Compliance', page: '13' },
                { num: '11', title: 'Getting Started', page: '14' },
                { num: '12', title: 'Contact & Support', page: '15' },
              ].map((item, i) => (
                <div key={i} className="flex items-center py-4 border-b border-slate-100 group hover:bg-slate-50 transition-colors">
                  <span className="w-12 text-2xl font-bold text-blue-600">{item.num}</span>
                  <span className="flex-1 text-lg text-slate-700 font-medium">{item.title}</span>
                  <span className="w-16 text-right text-slate-400">{item.page}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ==================== ABOUT PAGE ==================== */}
        <div className="manual-page page-break">
          <div className="h-2 bg-gradient-to-r from-blue-600 to-blue-400"></div>
          <div className="px-16 py-12">
            <div className="mb-2 text-blue-600 font-medium text-sm tracking-wider">CHAPTER 01</div>
            <h2 className="text-4xl font-bold text-slate-900 mb-8">About EDUBRIDGE NETWORK</h2>
            
            <div className="grid grid-cols-2 gap-8 mb-12">
              <div>
                <p className="text-lg text-slate-600 leading-relaxed mb-6">
                  EDUBRIDGE NETWORK is a cutting-edge, cloud-based Customer Relationship Management platform specifically designed for the international education sector.
                </p>
                <p className="text-slate-600 leading-relaxed">
                  Built with artificial intelligence at its core, the platform empowers educational consultancies, recruitment agencies, and university partnerships to streamline their operations, enhance student experiences, and drive business growth.
                </p>
              </div>
              <div className="bg-gradient-to-br from-blue-50 to-slate-50 rounded-2xl p-8">
                <h3 className="text-lg font-semibold text-slate-800 mb-4">Our Mission</h3>
                <p className="text-slate-600 leading-relaxed">
                  To revolutionize the international education consultancy industry through intelligent automation, seamless collaboration, and data-driven insights.
                </p>
              </div>
            </div>
            
            {/* Key Stats */}
            <div className="grid grid-cols-4 gap-6 mb-12">
              {[
                { value: '10+', label: 'Universities' },
                { value: '10K+', label: 'Applications' },
                { value: '99.9%', label: 'Uptime' },
                { value: '24/7', label: 'Support' },
              ].map((stat, i) => (
                <div key={i} className="text-center p-6 bg-white border border-slate-200 rounded-xl">
                  <div className="text-3xl font-bold text-blue-600 mb-1">{stat.value}</div>
                  <div className="text-sm text-slate-500">{stat.label}</div>
                </div>
              ))}
            </div>
            
            {/* Company Info */}
            <div className="bg-slate-900 rounded-2xl p-8 text-white">
              <h3 className="text-lg font-semibold mb-6 flex items-center gap-2">
                <svg className="w-5 h-5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                Ownership
              </h3>
              <div className="grid grid-cols-2 gap-8">
                <div>
                  <div className="text-blue-400 text-sm mb-2">Technology Partner</div>
                  <div className="text-xl font-semibold">Cloud Lab Private Ltd</div>
                  <p className="text-slate-400 text-sm mt-2">Leading software development company specializing in AI and cloud solutions.</p>
                  <a href="https://www.cloudlabweb.com" className="text-blue-400 text-sm mt-2 inline-block hover:underline">www.cloudlabweb.com</a>
                </div>
                <div>
                  <div className="text-blue-400 text-sm mb-2">Education Partner</div>
                  <div className="text-xl font-semibold">International Education Office (IEO) – UK</div>
                  <p className="text-slate-400 text-sm mt-2">Expertise in international student recruitment and university partnerships.</p>
                  <a href="https://www.ieouk.co.uk" className="text-blue-400 text-sm mt-2 inline-block hover:underline">www.ieouk.co.uk</a>
                </div>
              </div>
            </div>
          </div>
          <div className="absolute bottom-8 right-16 text-sm text-slate-400">04</div>
        </div>

        {/* ==================== PLATFORM OVERVIEW ==================== */}
        <div className="manual-page page-break">
          <div className="h-2 bg-gradient-to-r from-blue-600 to-blue-400"></div>
          <div className="px-16 py-12">
            <div className="mb-2 text-blue-600 font-medium text-sm tracking-wider">CHAPTER 02</div>
            <h2 className="text-4xl font-bold text-slate-900 mb-8">Platform Overview</h2>
            
            <div className="mb-10">
              <p className="text-lg text-slate-600 leading-relaxed">
                EDUBRIDGE NETWORK provides a comprehensive ecosystem for managing the entire student journey—from initial inquiry to enrollment and beyond.
              </p>
            </div>
            
            {/* Architecture Diagram */}
            <div className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl p-8 mb-10">
              <h3 className="text-lg font-semibold text-slate-800 mb-6 text-center">System Architecture</h3>
              <div className="flex items-center justify-center gap-4">
                <div className="text-center">
                  <div className="w-20 h-20 bg-white rounded-xl shadow-md flex items-center justify-center mb-2 mx-auto">
                    <svg className="w-10 h-10 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <span className="text-xs text-slate-600">Students</span>
                </div>
                <svg className="w-8 h-8 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
                <div className="text-center">
                  <div className="w-24 h-24 bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl shadow-lg flex items-center justify-center mb-2 mx-auto">
                    <span className="text-white font-bold text-sm">EDUBRIDGE NETWORK</span>
                  </div>
                  <span className="text-xs text-slate-600">AI Platform</span>
                </div>
                <svg className="w-8 h-8 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
                <div className="text-center">
                  <div className="w-20 h-20 bg-white rounded-xl shadow-md flex items-center justify-center mb-2 mx-auto">
                    <svg className="w-10 h-10 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  <span className="text-xs text-slate-600">Universities</span>
                </div>
              </div>
            </div>
            
            {/* Key Components */}
            <h3 className="text-xl font-semibold text-slate-800 mb-6">Key Components</h3>
            <div className="grid grid-cols-2 gap-6">
              {[
                { icon: '🎯', title: 'Lead Management', desc: 'Capture, track, and nurture student inquiries' },
                { icon: '📋', title: 'Application Pipeline', desc: 'End-to-end application tracking and processing' },
                { icon: '🤖', title: 'AI Counselor', desc: 'Intelligent form filling and data validation' },
                { icon: '📊', title: 'Analytics Dashboard', desc: 'Real-time insights and performance metrics' },
                { icon: '📄', title: 'Document Management', desc: 'Secure document storage and verification' },
                { icon: '💬', title: 'Communication Hub', desc: 'Integrated chat, email, and notifications' },
              ].map((item, i) => (
                <div key={i} className="flex gap-4 p-4 bg-white border border-slate-200 rounded-xl no-break">
                  <div className="text-3xl">{item.icon}</div>
                  <div>
                    <h4 className="font-semibold text-slate-800">{item.title}</h4>
                    <p className="text-sm text-slate-500">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="absolute bottom-8 right-16 text-sm text-slate-400">05</div>
        </div>

        {/* ==================== CORE MODULES ==================== */}
        <div className="manual-page page-break">
          <div className="h-2 bg-gradient-to-r from-blue-600 to-blue-400"></div>
          <div className="px-16 py-12">
            <div className="mb-2 text-blue-600 font-medium text-sm tracking-wider">CHAPTER 03</div>
            <h2 className="text-4xl font-bold text-slate-900 mb-8">Core Modules</h2>
            
            <p className="text-lg text-slate-600 leading-relaxed mb-10">
              EDUBRIDGE NETWORK is built on a modular architecture, allowing organizations to leverage the features most relevant to their operations.
            </p>
            
            {/* Module Cards */}
            <div className="space-y-6">
              {[
                { 
                  color: 'blue',
                  icon: (
                    <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  ),
                  title: 'Application Management',
                  desc: 'Complete lifecycle management of student applications with status tracking, document collection, and automated workflows.',
                  features: ['Multi-stage pipeline', 'Document verification', 'Status automation']
                },
                { 
                  color: 'purple',
                  icon: (
                    <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  ),
                  title: 'AI Counselor',
                  desc: 'Intelligent assistant that auto-fills application forms, validates data, and provides credibility assessments.',
                  features: ['Smart form filling', 'Data validation', 'Credibility reports']
                },
                { 
                  color: 'green',
                  icon: (
                    <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  ),
                  title: 'BPO Services',
                  desc: 'Outsourced processing services with credibility checks, verification workflows, and billing management.',
                  features: ['Credibility checks', 'Verification workflow', 'Automated billing']
                },
                { 
                  color: 'orange',
                  icon: (
                    <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  ),
                  title: 'Reporting & Analytics',
                  desc: 'Comprehensive dashboards with real-time metrics, conversion tracking, and performance analysis.',
                  features: ['Real-time dashboards', 'Custom reports', 'Team analytics']
                },
              ].map((module, i) => (
                <div key={i} className={`p-6 rounded-2xl border-l-4 bg-${module.color}-50 border-${module.color}-500 no-break`} 
                     style={{ 
                       backgroundColor: module.color === 'blue' ? '#eff6ff' : 
                                       module.color === 'purple' ? '#faf5ff' : 
                                       module.color === 'green' ? '#f0fdf4' : '#fff7ed',
                       borderLeftColor: module.color === 'blue' ? '#3b82f6' : 
                                        module.color === 'purple' ? '#a855f7' : 
                                        module.color === 'green' ? '#22c55e' : '#f97316'
                     }}>
                  <div className="flex gap-4">
                    <div className={`w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0`}
                         style={{ 
                           backgroundColor: module.color === 'blue' ? '#3b82f6' : 
                                           module.color === 'purple' ? '#a855f7' : 
                                           module.color === 'green' ? '#22c55e' : '#f97316',
                           color: 'white'
                         }}>
                      {module.icon}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-slate-800 mb-2">{module.title}</h3>
                      <p className="text-slate-600 mb-3">{module.desc}</p>
                      <div className="flex gap-2">
                        {module.features.map((f, j) => (
                          <span key={j} className="px-3 py-1 bg-white rounded-full text-xs text-slate-600 shadow-sm">{f}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="absolute bottom-8 right-16 text-sm text-slate-400">06</div>
        </div>

        {/* ==================== APPLICATION MANAGEMENT ==================== */}
        <div className="manual-page page-break">
          <div className="h-2 bg-gradient-to-r from-blue-600 to-blue-400"></div>
          <div className="px-16 py-12">
            <div className="mb-2 text-blue-600 font-medium text-sm tracking-wider">CHAPTER 04</div>
            <h2 className="text-4xl font-bold text-slate-900 mb-8">Application Management</h2>
            
            <p className="text-lg text-slate-600 leading-relaxed mb-8">
              The heart of EDUBRIDGE NETWORK - a powerful system for managing student applications from inquiry to enrollment.
            </p>
            
            {/* Pipeline Visualization */}
            <div className="bg-slate-50 rounded-2xl p-8 mb-8">
              <h3 className="text-lg font-semibold text-slate-800 mb-6">Application Pipeline</h3>
              <div className="flex items-center justify-between">
                {['Fresh Lead', 'Documents', 'Under Review', 'Interview', 'Offer', 'Enrolled'].map((stage, i) => (
                  <React.Fragment key={i}>
                    <div className="text-center">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold mb-2 mx-auto ${
                        i < 3 ? 'bg-blue-500' : i < 5 ? 'bg-blue-600' : 'bg-green-500'
                      }`}>
                        {i + 1}
                      </div>
                      <span className="text-xs text-slate-600">{stage}</span>
                    </div>
                    {i < 5 && (
                      <div className="flex-1 h-0.5 bg-slate-200 mx-2">
                        <div className="h-full bg-blue-400" style={{ width: `${100 - i * 20}%` }}></div>
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
            
            {/* Features Grid */}
            <div className="grid grid-cols-2 gap-6">
              {[
                { title: 'Multi-Tab Organization', desc: 'Fresh leads, active applications, and closed cases organized in intuitive tabs.', icon: '📑' },
                { title: 'Custom Statuses', desc: 'Define your own application statuses to match your workflow.', icon: '🏷️' },
                { title: 'Document Collection', desc: 'Request, upload, and verify required documents with ease.', icon: '📎' },
                { title: 'Team Assignment', desc: 'Assign counselors and managers to applications for accountability.', icon: '👥' },
                { title: 'Duplicate Detection', desc: 'Automatically detect and manage duplicate applications.', icon: '🔍' },
                { title: 'Bulk Actions', desc: 'Perform mass updates, assignments, and exports efficiently.', icon: '⚡' },
              ].map((item, i) => (
                <div key={i} className="flex gap-4 p-5 bg-white border border-slate-200 rounded-xl no-break">
                  <div className="text-2xl">{item.icon}</div>
                  <div>
                    <h4 className="font-semibold text-slate-800 mb-1">{item.title}</h4>
                    <p className="text-sm text-slate-500">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="absolute bottom-8 right-16 text-sm text-slate-400">07</div>
        </div>

        {/* ==================== AI COUNSELOR ==================== */}
        <div className="manual-page page-break">
          <div className="h-2 bg-gradient-to-r from-blue-600 to-blue-400"></div>
          <div className="px-16 py-12">
            <div className="mb-2 text-blue-600 font-medium text-sm tracking-wider">CHAPTER 05</div>
            <h2 className="text-4xl font-bold text-slate-900 mb-8">AI Counselor</h2>
            
            <div className="grid grid-cols-5 gap-8 mb-10">
              <div className="col-span-3">
                <p className="text-lg text-slate-600 leading-relaxed mb-6">
                  The AI Counselor is your intelligent assistant that automates tedious tasks, ensures data accuracy, and provides valuable insights for every application.
                </p>
                <div className="space-y-4">
                  {[
                    'Automatically extracts information from uploaded documents',
                    'Pre-fills application forms with validated data',
                    'Generates comprehensive credibility reports',
                    'Identifies potential issues before submission',
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span className="text-slate-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="col-span-2">
                <div className="bg-gradient-to-br from-blue-600 to-amber-500 rounded-2xl p-6 text-white">
                  <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center mb-4">
                    <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold mb-2">Powered by Advanced AI Models</h3>
                  <p className="text-white/80 text-sm">State-of-the-art language models for intelligent data processing and analysis.</p>
                </div>
              </div>
            </div>
            
            {/* Credibility Report Section */}
            <div className="bg-slate-900 rounded-2xl p-8 text-white">
              <h3 className="text-xl font-semibold mb-6 flex items-center gap-3">
                <svg className="w-6 h-6 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                AI Credibility Report
              </h3>
              <div className="grid grid-cols-3 gap-6">
                <div className="bg-white/10 rounded-xl p-5">
                  <div className="text-3xl font-bold text-green-400 mb-2">85%</div>
                  <div className="text-sm text-slate-300">Credibility Score</div>
                  <p className="text-xs text-slate-400 mt-2">Based on document verification and data consistency</p>
                </div>
                <div className="bg-white/10 rounded-xl p-5">
                  <div className="text-3xl font-bold text-blue-400 mb-2">12</div>
                  <div className="text-sm text-slate-300">Verification Checks</div>
                  <p className="text-xs text-slate-400 mt-2">Passport, NIC, English test, educational gap, etc.</p>
                </div>
                <div className="bg-white/10 rounded-xl p-5">
                  <div className="text-3xl font-bold text-amber-400 mb-2">PDF</div>
                  <div className="text-sm text-slate-300">Export Ready</div>
                  <p className="text-xs text-slate-400 mt-2">Download professional reports for review</p>
                </div>
              </div>
            </div>
          </div>
          <div className="absolute bottom-8 right-16 text-sm text-slate-400">08</div>
        </div>

        {/* ==================== BPO SERVICES ==================== */}
        <div className="manual-page page-break">
          <div className="h-2 bg-gradient-to-r from-blue-600 to-blue-400"></div>
          <div className="px-16 py-12">
            <div className="mb-2 text-blue-600 font-medium text-sm tracking-wider">CHAPTER 06</div>
            <h2 className="text-4xl font-bold text-slate-900 mb-8">BPO Services</h2>
            
            <p className="text-lg text-slate-600 leading-relaxed mb-8">
              Streamlined Business Process Outsourcing module for managing external verification services, credibility checks, and billing workflows.
            </p>
            
            {/* Hierarchy Diagram */}
            <div className="bg-gradient-to-br from-blue-50 to-slate-50 rounded-2xl p-8 mb-8">
              <h3 className="text-lg font-semibold text-slate-800 mb-6 text-center">Organization Hierarchy</h3>
              <div className="flex flex-col items-center gap-4">
                <div className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold shadow-lg">Workspace</div>
                <svg className="w-6 h-6 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                </svg>
                <div className="px-6 py-3 bg-blue-500 text-white rounded-lg font-semibold shadow-lg">University</div>
                <svg className="w-6 h-6 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                </svg>
                <div className="flex gap-8">
                  <div className="px-4 py-2 bg-blue-400 text-white rounded-lg text-sm shadow">Region</div>
                  <div className="px-4 py-2 bg-blue-400 text-white rounded-lg text-sm shadow">Managers</div>
                  <div className="px-4 py-2 bg-blue-400 text-white rounded-lg text-sm shadow">Agents</div>
                </div>
              </div>
            </div>
            
            {/* Features */}
            <div className="grid grid-cols-2 gap-6">
              {[
                { title: 'Credibility Certificate', desc: 'Generate CCN numbers and official credibility certificates', icon: '📜' },
                { title: 'Verification Workflow', desc: 'Multi-stage review process with status tracking', icon: '✅' },
                { title: 'Agent Portal', desc: 'Dedicated interface for BPO agents and managers', icon: '👨‍💼' },
                { title: 'Automated Billing', desc: 'Formula-based billing with invoice generation', icon: '💰' },
                { title: 'PDF Reports', desc: 'Professional credibility reports ready for download', icon: '📄' },
                { title: 'Re-review Process', desc: 'Request re-verification with updated documents', icon: '🔄' },
              ].map((item, i) => (
                <div key={i} className="flex gap-4 p-5 bg-white border border-slate-200 rounded-xl no-break">
                  <div className="text-2xl">{item.icon}</div>
                  <div>
                    <h4 className="font-semibold text-slate-800 mb-1">{item.title}</h4>
                    <p className="text-sm text-slate-500">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="absolute bottom-8 right-16 text-sm text-slate-400">09</div>
        </div>

        {/* ==================== REPORTING & ANALYTICS ==================== */}
        <div className="manual-page page-break">
          <div className="h-2 bg-gradient-to-r from-blue-600 to-blue-400"></div>
          <div className="px-16 py-12">
            <div className="mb-2 text-blue-600 font-medium text-sm tracking-wider">CHAPTER 07</div>
            <h2 className="text-4xl font-bold text-slate-900 mb-8">Reporting & Analytics</h2>
            
            <p className="text-lg text-slate-600 leading-relaxed mb-8">
              Make data-driven decisions with comprehensive dashboards, real-time metrics, and customizable reports.
            </p>
            
            {/* Dashboard Preview */}
            <div className="bg-slate-900 rounded-2xl p-6 mb-8">
              <div className="grid grid-cols-4 gap-4 mb-6">
                {[
                  { label: 'Total Applications', value: '1,247', change: '+12%', color: 'blue' },
                  { label: 'Conversion Rate', value: '23.5%', change: '+3.2%', color: 'green' },
                  { label: 'Pending Review', value: '84', change: '-8', color: 'yellow' },
                  { label: 'This Month', value: '156', change: '+24%', color: 'purple' },
                ].map((stat, i) => (
                  <div key={i} className="bg-white/10 rounded-xl p-4">
                    <div className="text-sm text-slate-400 mb-1">{stat.label}</div>
                    <div className="text-2xl font-bold text-white">{stat.value}</div>
                    <div className={`text-sm mt-1 ${stat.color === 'green' || stat.change.includes('+') ? 'text-green-400' : 'text-red-400'}`}>
                      {stat.change}
                    </div>
                  </div>
                ))}
              </div>
              <div className="h-32 bg-white/5 rounded-xl flex items-end justify-around p-4">
                {[40, 65, 45, 80, 60, 75, 55, 90, 70, 85, 60, 95].map((h, i) => (
                  <div key={i} className="w-6 bg-gradient-to-t from-blue-600 to-blue-400 rounded-t" style={{ height: `${h}%` }}></div>
                ))}
              </div>
            </div>
            
            {/* Report Types */}
            <h3 className="text-xl font-semibold text-slate-800 mb-4">Available Reports</h3>
            <div className="grid grid-cols-3 gap-4">
              {[
                'Application Summary',
                'Conversion Funnel',
                'Team Performance',
                'University Stats',
                'Country Analysis',
                'Revenue Report',
                'Source Tracking',
                'Timeline Analysis',
                'Custom Reports',
              ].map((report, i) => (
                <div key={i} className="px-4 py-3 bg-slate-50 rounded-lg text-sm text-slate-700 flex items-center gap-2">
                  <svg className="w-4 h-4 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  {report}
                </div>
              ))}
            </div>
          </div>
          <div className="absolute bottom-8 right-16 text-sm text-slate-400">10</div>
        </div>

        {/* ==================== STUDENT PORTAL ==================== */}
        <div className="manual-page page-break">
          <div className="h-2 bg-gradient-to-r from-blue-600 to-blue-400"></div>
          <div className="px-16 py-12">
            <div className="mb-2 text-blue-600 font-medium text-sm tracking-wider">CHAPTER 08</div>
            <h2 className="text-4xl font-bold text-slate-900 mb-8">Student Portal</h2>
            
            <div className="grid grid-cols-2 gap-8 mb-10">
              <div>
                <p className="text-lg text-slate-600 leading-relaxed mb-6">
                  A dedicated self-service portal where students can submit applications, upload documents, track progress, and communicate with counselors.
                </p>
                <div className="space-y-4">
                  {[
                    'Self-service application submission',
                    'Real-time status tracking',
                    'Secure document upload',
                    'Direct messaging with counselors',
                    'Notification alerts',
                    'Mobile-friendly interface',
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                      <span className="text-slate-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-gradient-to-br from-slate-100 to-blue-50 rounded-2xl p-6">
                <div className="bg-white rounded-xl shadow-lg p-4">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <div>
                      <div className="font-semibold text-slate-800">Welcome, Student</div>
                      <div className="text-xs text-slate-500">Your applications dashboard</div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between p-2 bg-green-50 rounded-lg text-sm">
                      <span>University of London</span>
                      <span className="text-green-600 font-medium">Offer Received</span>
                    </div>
                    <div className="flex justify-between p-2 bg-blue-50 rounded-lg text-sm">
                      <span>Manchester University</span>
                      <span className="text-blue-600 font-medium">Under Review</span>
                    </div>
                    <div className="flex justify-between p-2 bg-yellow-50 rounded-lg text-sm">
                      <span>Oxford College</span>
                      <span className="text-yellow-600 font-medium">Documents Needed</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Portal Features */}
            <div className="bg-slate-50 rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Portal Capabilities</h3>
              <div className="grid grid-cols-4 gap-4">
                {[
                  { icon: '📝', label: 'Apply Online' },
                  { icon: '📤', label: 'Upload Docs' },
                  { icon: '👁️', label: 'Track Status' },
                  { icon: '💬', label: 'Chat Support' },
                ].map((item, i) => (
                  <div key={i} className="text-center p-4 bg-white rounded-xl">
                    <div className="text-3xl mb-2">{item.icon}</div>
                    <div className="text-sm font-medium text-slate-700">{item.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="absolute bottom-8 right-16 text-sm text-slate-400">11</div>
        </div>

        {/* ==================== KEY BENEFITS ==================== */}
        <div className="manual-page page-break">
          <div className="h-2 bg-gradient-to-r from-blue-600 to-blue-400"></div>
          <div className="px-16 py-12">
            <div className="mb-2 text-blue-600 font-medium text-sm tracking-wider">CHAPTER 09</div>
            <h2 className="text-4xl font-bold text-slate-900 mb-8">Key Benefits</h2>
            
            <div className="grid grid-cols-2 gap-8">
              {[
                { 
                  icon: '⚡', 
                  title: 'Increased Efficiency',
                  desc: 'Automate repetitive tasks and reduce manual data entry by up to 70%. Process more applications with the same team.',
                  stat: '70%',
                  statLabel: 'Time Saved'
                },
                { 
                  icon: '🎯', 
                  title: 'Higher Conversion',
                  desc: 'AI-powered insights help counselors focus on high-potential leads, improving conversion rates significantly.',
                  stat: '35%',
                  statLabel: 'Better Conversion'
                },
                { 
                  icon: '🔒', 
                  title: 'Reduced Errors',
                  desc: 'Automated validation and credibility checks ensure data accuracy and reduce application rejections.',
                  stat: '90%',
                  statLabel: 'Error Reduction'
                },
                { 
                  icon: '📊', 
                  title: 'Better Insights',
                  desc: 'Real-time dashboards and analytics provide visibility into every aspect of your operations.',
                  stat: '100%',
                  statLabel: 'Visibility'
                },
                { 
                  icon: '🤝', 
                  title: 'Improved Collaboration',
                  desc: 'Team chat, notifications, and assignment workflows keep everyone aligned and accountable.',
                  stat: '24/7',
                  statLabel: 'Connected'
                },
                { 
                  icon: '🚀', 
                  title: 'Scalable Growth',
                  desc: 'Cloud-based infrastructure grows with your business without additional IT overhead.',
                  stat: '∞',
                  statLabel: 'Scalability'
                },
              ].map((benefit, i) => (
                <div key={i} className="p-6 bg-white border border-slate-200 rounded-2xl no-break">
                  <div className="flex justify-between items-start mb-4">
                    <div className="text-4xl">{benefit.icon}</div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-blue-600">{benefit.stat}</div>
                      <div className="text-xs text-slate-500">{benefit.statLabel}</div>
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold text-slate-800 mb-2">{benefit.title}</h3>
                  <p className="text-slate-600">{benefit.desc}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="absolute bottom-8 right-16 text-sm text-slate-400">12</div>
        </div>

        {/* ==================== SECURITY & COMPLIANCE ==================== */}
        <div className="manual-page page-break">
          <div className="h-2 bg-gradient-to-r from-blue-600 to-blue-400"></div>
          <div className="px-16 py-12">
            <div className="mb-2 text-blue-600 font-medium text-sm tracking-wider">CHAPTER 10</div>
            <h2 className="text-4xl font-bold text-slate-900 mb-8">Security & Compliance</h2>
            
            <p className="text-lg text-slate-600 leading-relaxed mb-8">
              EDUBRIDGE NETWORK is built with enterprise-grade security and compliance standards to protect your data and your students' privacy.
            </p>
            
            <div className="grid grid-cols-3 gap-6 mb-8">
              {[
                { icon: '🔐', title: 'Data Encryption', desc: 'AES-256 encryption for data at rest and TLS 1.3 for data in transit' },
                { icon: '🛡️', title: 'Access Control', desc: 'Role-based permissions with multi-factor authentication' },
                { icon: '📋', title: 'Audit Logs', desc: 'Complete audit trail of all system activities' },
                { icon: '🌐', title: 'GDPR Compliant', desc: 'Full compliance with EU data protection regulations' },
                { icon: '☁️', title: 'Cloud Security', desc: 'Hosted on secure AWS infrastructure with 99.9% uptime' },
                { icon: '🔄', title: 'Backup & Recovery', desc: 'Automated daily backups with disaster recovery' },
              ].map((item, i) => (
                <div key={i} className="p-5 bg-slate-50 rounded-xl text-center no-break">
                  <div className="text-3xl mb-3">{item.icon}</div>
                  <h4 className="font-semibold text-slate-800 mb-2">{item.title}</h4>
                  <p className="text-sm text-slate-600">{item.desc}</p>
                </div>
              ))}
            </div>
            
            <div className="bg-slate-900 rounded-2xl p-8 text-white">
              <h3 className="text-xl font-semibold mb-6">Compliance Certifications</h3>
              <div className="flex justify-around">
                {['GDPR', 'ISO 27001', 'SOC 2', 'HIPAA Ready'].map((cert, i) => (
                  <div key={i} className="text-center">
                    <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mb-2 mx-auto">
                      <svg className="w-8 h-8 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                    </div>
                    <div className="font-semibold">{cert}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="absolute bottom-8 right-16 text-sm text-slate-400">13</div>
        </div>

        {/* ==================== GETTING STARTED ==================== */}
        <div className="manual-page page-break">
          <div className="h-2 bg-gradient-to-r from-blue-600 to-blue-400"></div>
          <div className="px-16 py-12">
            <div className="mb-2 text-blue-600 font-medium text-sm tracking-wider">CHAPTER 11</div>
            <h2 className="text-4xl font-bold text-slate-900 mb-8">Getting Started</h2>
            
            <p className="text-lg text-slate-600 leading-relaxed mb-8">
              Get up and running with EDUBRIDGE NETWORK in just a few simple steps.
            </p>
            
            {/* Steps */}
            <div className="space-y-6 mb-10">
              {[
                { num: '01', title: 'Sign Up', desc: 'Create your organization account and set up your workspace' },
                { num: '02', title: 'Configure', desc: 'Customize settings, add team members, and define workflows' },
                { num: '03', title: 'Import Data', desc: 'Migrate existing data using our import tools or API' },
                { num: '04', title: 'Train Team', desc: 'Quick onboarding session to get your team up to speed' },
                { num: '05', title: 'Go Live', desc: 'Start processing applications and growing your business' },
              ].map((step, i) => (
                <div key={i} className="flex gap-6 items-start no-break">
                  <div className="w-14 h-14 bg-blue-600 rounded-xl flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                    {step.num}
                  </div>
                  <div className="flex-1 pt-2">
                    <h3 className="text-xl font-semibold text-slate-800 mb-1">{step.title}</h3>
                    <p className="text-slate-600">{step.desc}</p>
                  </div>
                  {i < 4 && (
                    <div className="absolute left-[70px] mt-16 w-0.5 h-8 bg-blue-200"></div>
                  )}
                </div>
              ))}
            </div>
            
            <div className="bg-blue-50 rounded-2xl p-8">
              <h3 className="text-xl font-semibold text-slate-800 mb-4">Ready to Transform Your Business?</h3>
              <p className="text-slate-600 mb-6">
                Schedule a personalized demo to see how EDUBRIDGE NETWORK can streamline your operations and boost your growth.
              </p>
              <div className="flex gap-4">
                <div className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold">Request Demo</div>
                <div className="px-6 py-3 bg-white border border-slate-200 rounded-lg font-semibold text-slate-700">View Pricing</div>
              </div>
            </div>
          </div>
          <div className="absolute bottom-8 right-16 text-sm text-slate-400">14</div>
        </div>

        {/* ==================== CONTACT PAGE ==================== */}
        <div className="manual-page page-break">
          <div className="h-2 bg-gradient-to-r from-blue-600 to-blue-400"></div>
          <div className="px-16 py-12">
            <div className="mb-2 text-blue-600 font-medium text-sm tracking-wider">CHAPTER 12</div>
            <h2 className="text-4xl font-bold text-slate-900 mb-8">Contact & Support</h2>
            
            <div className="grid grid-cols-2 gap-8 mb-10">
              <div>
                <h3 className="text-xl font-semibold text-slate-800 mb-6">Get in Touch</h3>
                <div className="space-y-6">
                  <div className="flex gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                      </svg>
                    </div>
                    <div>
                      <div className="text-sm text-slate-500">Website</div>
                      <div className="font-semibold text-slate-800">edubridgenetwork.com</div>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <div className="text-sm text-slate-500">Email</div>
                      <div className="font-semibold text-slate-800">support@edubridgenetwork.com</div>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                    </div>
                    <div>
                      <div className="text-sm text-slate-500">Phone</div>
                      <div className="font-semibold text-slate-800">+44 738 0885 06</div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-slate-900 rounded-2xl p-8 text-white">
                <h3 className="text-xl font-semibold mb-4">Support Hours</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Monday - Friday</span>
                    <span>9:00 AM - 6:00 PM GMT</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Saturday</span>
                    <span>10:00 AM - 4:00 PM GMT</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Sunday</span>
                    <span>Closed</span>
                  </div>
                </div>
                <div className="mt-6 pt-6 border-t border-white/20">
                  <div className="text-sm text-slate-400 mb-2">Emergency Support</div>
                  <div className="font-semibold">24/7 for Enterprise Customers</div>
                </div>
              </div>
            </div>
            
            {/* Footer Section */}
            <div className="border-t border-slate-200 pt-8 mt-auto">
              <div className="flex justify-between items-end">
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <span className="text-xl font-bold text-slate-800">EDUBRIDGE NETWORK</span>
                  </div>
                  <p className="text-sm text-slate-500">
                    © 2025 Cloud Lab Private Ltd & IEO UK. All rights reserved.
                  </p>
                </div>
                <div className="text-right text-sm text-slate-400">
                  <div>Product Manual v2.0</div>
                  <div>Last Updated: January 2025</div>
                </div>
              </div>
            </div>
          </div>
          <div className="absolute bottom-8 right-16 text-sm text-slate-400">15</div>
        </div>

      </div>
    </>
  );
};

export default ProductManualPage;
